import { ChevronLeft as ChevronLeftIcon, X as XIcon } from "@tamagui/lucide-icons";
import { Button, Dialog, type DialogContentProps, Headline, isWeb, Paragraph, Portal, ScrollView, type ScrollViewProps, Sheet, type SheetProps, type SnapPointsMode, Stack, Title, useSafeAreaInsets, useSheetContainer, useWindowDimensions, VisuallyHidden, XStack, YStack, type YStackProps } from "@medbillai/ui";
import { BackgroundRadialGrantedGradient } from "./BackgroundRadialGrantedGradient";
export type AdaptiveDialogSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  backgroundRadialGradient?: boolean;
  title: string;
  dialogContentProps?: DialogContentProps;
  hideTitle?: boolean;
  // TODO(ENG-5674): Remove this prop, making the new styles the default, and
  // migrate any uses of it to the new styles.
  newTitleStyle?: boolean;
  showBackButton?: boolean;
  onBackButtonPress?: () => void;
  snapPoints?: (string | number)[] | undefined;
  snapPointsMode?: SnapPointsMode | undefined;
  zIndex?: number;
  contentWrapperProps?: YStackProps;
  disableScroll?: boolean;
  sheetProps?: SheetProps;
  scrollViewProps?: Partial<ScrollViewProps>;
  absolutePositionTitle?: boolean;
  hideClose?: boolean;
};
/**
 * AdaptiveDialogSheet is a responsive component that renders differently based on the screen size:
 * - On mobile devices and narrow screens, it displays as a bottom sheet that slides up
 * - On web/desktop screens, it displays as a centered modal dialog
 *
 * This provides a consistent user experience while following platform conventions.
 * Use this component when you need an interactive dialog that adapts between mobile and web.
 */
export const AdaptiveDialogSheet = ({
  open,
  onOpenChange,
  children,
  backgroundRadialGradient = false,
  title,
  dialogContentProps,
  hideTitle = false,
  newTitleStyle = false,
  showBackButton = false,
  onBackButtonPress,
  snapPoints,
  snapPointsMode,
  zIndex = 100000,
  contentWrapperProps,
  disableScroll = false,
  sheetProps,
  scrollViewProps,
  absolutePositionTitle = false,
  hideClose = false
}: AdaptiveDialogSheetProps) => {
  const {
    bottom
  } = useSafeAreaInsets();
  const containerComponent = useSheetContainer();
  const {
    width,
    height
  } = useWindowDimensions();
  const shouldAdapt = isWeb && (width < 800 || height < 630) || !isWeb;
  const TitleWrapper = shouldAdapt ? Title : Dialog.Title;
  const sheetContent = <>
      <BackgroundRadialGrantedGradient animation="200ms" opacity={backgroundRadialGradient ? 1 : 0} y={backgroundRadialGradient ? 0 : 10} />
      <XStack width="100%" justifyContent="space-between" alignItems="center" p="$3" {...absolutePositionTitle && {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      zIndex: 1,
      opacity: 1
    }}>
        {showBackButton ? <Button unstyled onPress={onBackButtonPress} cursor="pointer" icon={() => <ChevronLeftIcon width="$2" strokeWidth={2} color="$textSecondary" />}></Button> : <Stack width="$2" />}
        {!(hideTitle || newTitleStyle) ? <TitleWrapper display="flex" alignItems="center">
            <Paragraph>{title}</Paragraph>
          </TitleWrapper> : <VisuallyHidden>
            <TitleWrapper>{title}</TitleWrapper>
          </VisuallyHidden>}
        {hideClose ? <Stack width="$2" /> : <Button unstyled onPress={() => onOpenChange(false)} cursor="pointer" icon={() => <XIcon width="$2" strokeWidth={2} color="$textSecondary" />} />}
      </XStack>
      <YStack width="100%" gap="$4" pb="$3" flex={1} px="$3" {...contentWrapperProps}>
        <ScrollView scrollEnabled={!disableScroll} flex={1}
      // We need to add padding so that if the content has a border, it
      // doesn't get cut off by the scrollbar
      pb="$2" px="$1" {...scrollViewProps}>
          {newTitleStyle ? <Headline
        // This ideally would be baked into the Headline component, but
        // that line height math has been a little screwy since we
        // switched to KH Teka.
        lineHeight={24} size="$7" width="100%" paddingBottom="$3.5">
              {title}
            </Headline> : null}
          {children}
        </ScrollView>
      </YStack>
    </>;
  const adaptedContent = <Portal>
      <Sheet modal={false} snapPoints={snapPointsMode === "percent" ? snapPoints : undefined} snapPointsMode={snapPointsMode ?? "fit"} dismissOnSnapToBottom open={open} onOpenChange={onOpenChange} animation="medium" zIndex={zIndex} native containerComponent={containerComponent} {...sheetProps}>
        <Sheet.Overlay enterStyle={{
        opacity: 0
      }} exitStyle={{
        opacity: 0
      }} animation="medium" zIndex={zIndex - 100} />
        {/* 
          "Why do we need this YStack?"
          The Sheet.Frame renders a "bottom overflow" element after our content,
          for when animating or dragging past 100%.
          If our content has a height that is not a whole number of pixels,
          there will be a gap between it and this bottom overflow element, showing
          the background color of the containing element.
          Tamagui does not expose a way to set that containing background color
          (the Sheet.Frame's background color is forwarded to a part with just our
          content, not to an ancestor of our content+the bottom overflow element.)
           */}
        <YStack flex={1} backgroundColor="$background" borderTopLeftRadius="$8" borderTopRightRadius="$8" borderBottomWidth={0}>
          <Sheet.Frame mb={bottom}>{sheetContent}</Sheet.Frame>
        </YStack>
      </Sheet>
    </Portal>;
  return shouldAdapt ? adaptedContent : <Dialog open={open} onOpenChange={onOpenChange} modal data-sentry-element="Dialog" data-sentry-component="AdaptiveDialogSheet" data-sentry-source-file="AdaptiveDialogSheet.tsx">
      <Dialog.Portal zIndex={zIndex} data-sentry-element="Dialog.Portal" data-sentry-source-file="AdaptiveDialogSheet.tsx">
        <Dialog.Overlay key="overlay" animation="200ms easeCubic" enterStyle={{
        opacity: 0
      }} exitStyle={{
        opacity: 0
      }} backgroundColor="$background05" zIndex={zIndex - 100} data-sentry-element="Dialog.Overlay" data-sentry-source-file="AdaptiveDialogSheet.tsx" />

        <Dialog.Content bordered elevate key="content" animateOnly={["transform", "opacity", "height"]} animation="200ms easeCubic" enterStyle={{
        x: 0,
        y: -20,
        opacity: 0,
        scale: 0.9
      }} exitStyle={{
        x: 0,
        y: 10,
        opacity: 0,
        scale: 0.95
      }} gap="$4" borderRadius="$6" zIndex={zIndex} minWidth={600} minHeight={400} {...dialogContentProps} data-sentry-element="Dialog.Content" data-sentry-source-file="AdaptiveDialogSheet.tsx">
          {sheetContent}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>;
};